import { MIN_ROOMS_FOR_NOMINATOR, MIN_ROOMS_FOR_VOTER } from 'src/app/models/user.model';

export enum ApplicationWarningType {
  NAME_CASING,
  CITY_CASING,
  MISSING_SOCIAL_MEDIA,
  INSUFFICIENT_BIO,
  AFFILIATIONS_NOT_IN_BIO,
  MISSING_EXPERIENCE_EVIDENCE,
  INSUFFICIENT_GAME_COUNT
}

export interface ApplicationWarning {
  type: ApplicationWarningType;
  message: string;
}

export interface ApplicationReviewData {
  realName?: string;
  city?: string;
  facebookUsername?: string;
  instagramUsername?: string;
  linkedinUsername?: string;
  bio?: string;
  affiliations?: string;
  irlRoomCount?: number | string;
  isUpgrade?: boolean;
  roomCountEvidence?: string;
  mortyAppUsername?: string;
  escapeTheReviewUserId?: string;
  escapeAllGrUserId?: string;
  escapeGameFrUserId?: string;
  escapeRoomersUserId?: string;
  escapeTalkNlUserId?: string;
  lockMeUserId?: string;
  theEscapersUserId?: string;
}

const TITLE_CASE_PARTICLES = new Set([
  'a', 'af', 'al', 'av', 'bin', 'da', 'de', 'del', 'della', 'der', 'di', 'du',
  'e', 'el', 'la', 'le', 'of', 'the', 'van', 'von', 'y'
]);

const NO_AFFILIATION_PATTERN =
  /^(?:n\/a|n\.a\.|na|none|nil|nope|nothing|no|none listed|not applicable|no affiliation[s]?|not affiliated.*|none that i.*)$/i;

const ROLE_PREFIX_PATTERN =
  /^(?:former\s+|ex-)?(?:(?:co-)?(?:owner|designer|employee|investor|founder|manager|operator)s?)(?:\s+(?:of|at|for|with|from))?\s+/i;

const TRACKING_FIELDS = <const>[
  'mortyAppUsername',
  'escapeTheReviewUserId',
  'escapeAllGrUserId',
  'escapeGameFrUserId',
  'escapeRoomersUserId',
  'escapeTalkNlUserId',
  'lockMeUserId',
  'theEscapersUserId'
];

const URL_IN_TEXT_PATTERN =
  /(?:https?:\/\/|www\.)\S+|\S+\.(?:com|org|net|io|app|co|uk|gr|fr|nl|me)(?:\/\S*)?/i;

const MIN_BIO_WORDS = 20;
const MIN_BIO_SENTENCES = 2;
const BIO_ABBREVIATION_PATTERN = /\b(?:mr|mrs|ms|dr|prof|sr|jr|inc|ltd|co|vs|etc)\./gi;

const STEP_WARNING_TYPES: { [step: number]: ApplicationWarningType[] } = {
  1: [
    ApplicationWarningType.NAME_CASING,
    ApplicationWarningType.CITY_CASING,
    ApplicationWarningType.MISSING_SOCIAL_MEDIA,
    ApplicationWarningType.INSUFFICIENT_BIO
  ],
  2: [
    ApplicationWarningType.AFFILIATIONS_NOT_IN_BIO,
    ApplicationWarningType.MISSING_EXPERIENCE_EVIDENCE,
    ApplicationWarningType.INSUFFICIENT_GAME_COUNT
  ]
};

export function getApplicationWarnings(data: ApplicationReviewData): ApplicationWarning[] {
  const warnings: ApplicationWarning[] = [];
  if (hasText(data.realName) && !hasProperTitleCasing(data.realName)) {
    warnings.push({
      type: ApplicationWarningType.NAME_CASING,
      message: titleCasingWarning('name', data.realName)
    });
  }
  if (hasText(data.city) && !hasProperTitleCasing(data.city)) {
    warnings.push({
      type: ApplicationWarningType.CITY_CASING,
      message: titleCasingWarning('city', data.city)
    });
  }
  if (!hasSocialMedia(data)) {
    warnings.push({
      type: ApplicationWarningType.MISSING_SOCIAL_MEDIA,
      message: 'You have not included any social media accounts. Please add at least one (Facebook, Instagram, or LinkedIn) so we can find you online.'
    });
  }
  if (hasText(data.bio) && isInsufficientBio(data.bio)) {
    warnings.push({
      type: ApplicationWarningType.INSUFFICIENT_BIO,
      message: 'Your bio appears too short or is not written as multiple complete sentences. Please write a short paragraph of at least two sentences in English that introduces who you are and the role you play in the escape room community. Your bio must also include any escape room affiliations by name.'
    });
  }
  if (affiliationsMissingFromBio(data.affiliations, data.bio)) {
    warnings.push({
      type: ApplicationWarningType.AFFILIATIONS_NOT_IN_BIO,
      message: 'You listed affiliations that do not appear to be mentioned by name in your bio. Please include any escape room companies or rooms you are affiliated with in your public bio.'
    });
  }
  if (!hasExperienceEvidence(data)) {
    warnings.push({
      type: ApplicationWarningType.MISSING_EXPERIENCE_EVIDENCE,
      message: 'Please provide evidence of your experience by adding a room-tracking account and/or including a link to a list of rooms you have played in the evidence field.'
    });
  }
  const roomCountWarning = insufficientRoomCountWarning(data);
  if (roomCountWarning) {
    warnings.push(roomCountWarning);
  }
  return warnings;
}

export function getApplicationWarningsForStep(data: ApplicationReviewData, step: number): ApplicationWarning[] {
  const types = STEP_WARNING_TYPES[step];
  if (!types) {
    return [];
  }
  return getApplicationWarnings(data).filter(warning => types.includes(warning.type));
}

export function formatApplicationWarningsHtml(warnings: ApplicationWarning[]): string {
  return '<ul>' + warnings.map(warning => `<li>${warning.message}</li>`).join('') + '</ul>';
}

function hasText(value?: string): boolean {
  return Boolean(value && value.trim().length > 0);
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function titleCasingWarning(fieldLabel: string, value: string): string {
  const current = value.trim();
  const suggested = toSuggestedTitleCase(current);
  const suffix = 'since it will appear on the public website.';
  if (suggested && suggested !== current) {
    return `Your ${fieldLabel} should use proper title capitalization, ${suffix} Try "${escapeHtml(suggested)}" instead of "${escapeHtml(current)}".`;
  }
  return `Your ${fieldLabel} should use proper title capitalization (for example, "Jane Doe" rather than "JANE DOE" or "jane doe"), ${suffix}`;
}

function toSuggestedTitleCase(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);
  return words.map((word, index) => titleCaseWord(word, index === 0)).join(' ');
}

function titleCaseWord(word: string, isFirstWord: boolean): string {
  if (!isFirstWord && TITLE_CASE_PARTICLES.has(lettersOf(word).toLowerCase())) {
    return lowercaseLetters(word);
  }
  return word.split('-').map(part => titleCasePart(part)).join('-');
}

function titleCasePart(part: string): string {
  let out = '';
  let capitalizeNext = true;
  for (const ch of part) {
    if (/\p{L}/u.test(ch)) {
      out += capitalizeNext ? ch.toUpperCase() : ch.toLowerCase();
      capitalizeNext = false;
    } else {
      out += ch;
      if (ch === '\'' || ch === '\u2019') {
        capitalizeNext = true;
      }
    }
  }
  return applyMcPrefix(out);
}

function applyMcPrefix(value: string): string {
  const letters = lettersOf(value);
  if (letters.length < 3 || firstChar(letters) !== 'M' || firstChar(letters.slice(1)) !== 'c') {
    return value;
  }
  let letterCount = 0;
  let out = '';
  for (const ch of value) {
    if (/\p{L}/u.test(ch)) {
      letterCount++;
      if (letterCount === 3 && isLowerCasedLetter(ch)) {
        out += ch.toUpperCase();
        continue;
      }
    }
    out += ch;
  }
  return out;
}

function lowercaseLetters(value: string): string {
  let out = '';
  for (const ch of value) {
    out += /\p{L}/u.test(ch) ? ch.toLowerCase() : ch;
  }
  return out;
}

function hasSocialMedia(data: ApplicationReviewData): boolean {
  return hasText(data.facebookUsername) || hasText(data.instagramUsername) ||
    hasText(data.linkedinUsername);
}

function isInsufficientBio(bio?: string): boolean {
  const text = (bio || '').trim();
  if (!text) {
    return true;
  }
  return countWords(text) < MIN_BIO_WORDS || countSentences(text) < MIN_BIO_SENTENCES;
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function countSentences(text: string): number {
  const cleaned = text.replace(BIO_ABBREVIATION_PATTERN, ' ');
  return cleaned.split(/[.!?]+/).map(part => part.trim()).filter(part => part.length > 0).length;
}

function insufficientRoomCountWarning(data: ApplicationReviewData): ApplicationWarning | null {
  const count = Number(data.irlRoomCount);
  if (!Number.isFinite(count)) {
    return null;
  }
  const minRooms = data.isUpgrade ? MIN_ROOMS_FOR_NOMINATOR : MIN_ROOMS_FOR_VOTER;
  if (count >= minRooms) {
    return null;
  }
  const role = data.isUpgrade ? 'nominator' : 'voter';
  return {
    type: ApplicationWarningType.INSUFFICIENT_GAME_COUNT,
    message: `You have reported playing ${count} in-person rooms, but at least ${minRooms} are required to be accepted as a ${role}. If your count is incomplete, please update it; otherwise please wait until you reach this milestone before submitting.`
  };
}

function hasExperienceEvidence(data: ApplicationReviewData): boolean {
  if (TRACKING_FIELDS.some(field => hasText(data[field]))) {
    return true;
  }
  return URL_IN_TEXT_PATTERN.test(data.roomCountEvidence || '');
}

function isNoAffiliation(value: string): boolean {
  return NO_AFFILIATION_PATTERN.test(value.trim().replace(/[.!]+$/g, '').trim());
}

function affiliationsMissingFromBio(affiliations?: string, bio?: string): boolean {
  const aff = (affiliations || '').trim();
  if (!aff || isNoAffiliation(aff)) {
    return false;
  }
  const bioLower = (bio || '').toLowerCase();
  const items = aff.split(/[\n;,]+/).map(item => item.trim()).filter(Boolean);
  for (const item of items) {
    const withoutParens = item.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
    if (!withoutParens || isNoAffiliation(withoutParens)) {
      continue;
    }
    let candidate = withoutParens.replace(ROLE_PREFIX_PATTERN, '').trim();
    if (!candidate) {
      candidate = withoutParens;
    }
    if (candidate.length < 3) {
      continue;
    }
    if (!bioLower.includes(candidate.toLowerCase())) {
      return true;
    }
  }
  return false;
}

function isUpperCasedLetter(ch: string): boolean {
  return /\p{L}/u.test(ch) && ch === ch.toUpperCase() && ch !== ch.toLowerCase();
}

function isLowerCasedLetter(ch: string): boolean {
  return /\p{L}/u.test(ch) && ch === ch.toLowerCase() && ch !== ch.toUpperCase();
}

function lettersOf(value: string): string {
  let letters = '';
  for (const ch of value) {
    if (/\p{L}/u.test(ch)) {
      letters += ch;
    }
  }
  return letters;
}

function firstChar(value: string): string {
  const match = /^./u.exec(value);
  return match ? match[0] : '';
}

function hasProperTitleCasing(value: string): boolean {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return true;
  }
  let hasLower = false;
  let hasUpper = false;
  for (const ch of trimmed) {
    if (isUpperCasedLetter(ch)) {
      hasUpper = true;
    } else if (isLowerCasedLetter(ch)) {
      hasLower = true;
    }
  }
  if (!hasLower && !hasUpper) {
    return true;
  }
  if (!hasLower || !hasUpper) {
    return false;
  }
  const words = trimmed.split(/\s+/).filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    for (const part of words[i].split('-')) {
      const letterCore = lettersOf(part);
      if (!letterCore) {
        continue;
      }
      if (i > 0 && TITLE_CASE_PARTICLES.has(letterCore.toLowerCase())) {
        continue;
      }
      const firstLetter = firstChar(letterCore);
      if (!isUpperCasedLetter(firstLetter)) {
        return false;
      }
      const rest = letterCore.slice(firstLetter.length);
      let restCasedCount = 0;
      let restAllUpper = true;
      for (const ch of rest) {
        if (isUpperCasedLetter(ch)) {
          restCasedCount++;
        } else if (isLowerCasedLetter(ch)) {
          restCasedCount++;
          restAllUpper = false;
        }
      }
      if (restCasedCount >= 2 && restAllUpper) {
        return false;
      }
    }
  }
  return true;
}
